import React, { ReactNode } from 'react';
import {
  Autocomplete,
  TextField,
  AutocompleteProps,
  AutocompleteValue,
} from '@mui/material';

/**
 * Props for the ControlledAutocomplete component.
 * Extends AutocompleteProps from MUI, omitting 'value' and 'onChange'.
 */
interface ControlledAutocompleteProps<
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
> extends Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'value' | 'onChange' | 'renderInput'
> {
  selectedValue: AutocompleteValue<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  > | null;
  setSelectedValue: React.Dispatch<
    React.SetStateAction<AutocompleteValue<
      T,
      Multiple,
      DisableClearable,
      FreeSolo
    > | null>
  >;
  label: string;
  isDataTable?: boolean;
  onValueChange?: (
    value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
  ) => void;
  helperText?: ReactNode;
}

/**
 * A controlled autocomplete component using MUI's Autocomplete.
 * This component provides a customizable and reusable autocomplete with controlled state.
 *
 * @param {ControlledAutocompleteProps<T>} props - The props for the component
 * @returns {React.ReactElement} The rendered ControlledAutocomplete component
 */
const ControlledAutocomplete = <
  T,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false,
>({
  selectedValue,
  setSelectedValue,
  label,
  sx,
  isDataTable = true,
  onValueChange,
  helperText,
  ...props
}: ControlledAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => {
  return (
    <Autocomplete
      {...props}
      value={
        selectedValue ??
        (null as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>)
      }
      onChange={(_event, newValue) => {
        setSelectedValue(newValue);
        onValueChange?.(newValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputLabelProps={{ shrink: true }}
          sx={{
            ...(isDataTable && {
              '& .MuiInputBase-root': {
                border: '0!important',
              },
              '& .MuiOutlinedInput-root': {
                '& > fieldset': {
                  borderColor: '#EEE',
                },
              },
            }),
            ...sx,
          }}
          {...(helperText ? { helperText } : {})}
          autoComplete='new-password'
        />
      )}
      sx={{
        width: { xs: '100%', sm: 'auto' },
        ...sx,
      }}
    />
  );
};

export default ControlledAutocomplete;
